import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type RootState } from '../store'
import { type IntegrationServiceId } from '../../common/integrations'
import { type FileBrowserItem, type Breadcrumb, type FolderItemsPage, ROOT_ANCHOR_ID } from '../../components/DocumentFileBrowser/types'

interface DocumentFileBrowserState {
  serviceId: IntegrationServiceId | null
  currentItems: FileBrowserItem[]
  breadcrumbs: Breadcrumb[]
  totalCount: number | null
  nextCursor: string | null
  isInitialLoading: boolean
  isFolderLoading: boolean
  selectedIds: string[]
  searchQuery: string
  searchResults: FileBrowserItem[] | null
  searchTotalCount: number | null
  searchNextCursor: string | null
  isSearching: boolean
}

const initialState: DocumentFileBrowserState = {
  serviceId: null,
  currentItems: [],
  breadcrumbs: [{ id: ROOT_ANCHOR_ID, name: '' }],
  totalCount: null,
  nextCursor: null,
  isInitialLoading: false,
  isFolderLoading: false,
  selectedIds: [],
  searchQuery: '',
  searchResults: null,
  searchTotalCount: null,
  searchNextCursor: null,
  isSearching: false,
}

const withoutDuplicateIds = (existing: FileBrowserItem[], incoming: FileBrowserItem[]): FileBrowserItem[] => {
  const seenIds = new Set(existing.map((item) => item.id))
  return incoming.filter((item) => {
    if (seenIds.has(item.id)) {
      return false
    }
    seenIds.add(item.id)
    return true
  })
}

const sortItems = (items: FileBrowserItem[]): FileBrowserItem[] => {
  const folders = items.filter((i) => i.type === 'folder').sort((a, b) => a.name.localeCompare(b.name))
  const files = items.filter((i) => i.type === 'file').sort((a, b) => a.name.localeCompare(b.name))
  return [...folders, ...files]
}

export const documentFileBrowserSlice = createSlice({
  name: 'documentFileBrowser',
  initialState,
  reducers: {
    resetFileBrowser: () => initialState,

    setServiceId: (state, action: PayloadAction<IntegrationServiceId>) => {
      state.serviceId = action.payload
    },

    setFolderView: (state, action: PayloadAction<FolderItemsPage & { breadcrumbs: Breadcrumb[] }>) => {
      state.currentItems = withoutDuplicateIds([], action.payload.items)
      state.breadcrumbs = action.payload.breadcrumbs
      state.totalCount = action.payload.totalCount ?? null
      state.nextCursor = action.payload.nextCursor ?? null
      state.selectedIds = []
      state.searchQuery = ''
      state.searchResults = null
    },

    appendFolderItems: (state, action: PayloadAction<FolderItemsPage>) => {
      state.currentItems.push(...withoutDuplicateIds(state.currentItems, action.payload.items))
      state.totalCount = action.payload.totalCount ?? state.totalCount
      state.nextCursor = action.payload.nextCursor ?? null
    },

    setCurrentItems: (state, action: PayloadAction<FileBrowserItem[]>) => {
      state.currentItems = action.payload
    },

    setIsInitialLoading: (state, action: PayloadAction<boolean>) => {
      state.isInitialLoading = action.payload
    },

    setIsFolderLoading: (state, action: PayloadAction<boolean>) => {
      state.isFolderLoading = action.payload
    },

    toggleItemSelection: (state, action: PayloadAction<{ itemId: string; singleSelect: boolean }>) => {
      const { itemId, singleSelect } = action.payload
      if (singleSelect) {
        state.selectedIds = state.selectedIds.includes(itemId) ? [] : [itemId]
      } else if (state.selectedIds.includes(itemId)) {
        state.selectedIds = state.selectedIds.filter((id) => id !== itemId)
      } else {
        state.selectedIds.push(itemId)
      }
    },

    clearSelection: (state) => {
      state.selectedIds = []
    },

    selectAllItems: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },

    setSearchResults: (state, action: PayloadAction<FolderItemsPage | null>) => {
      state.searchResults = action.payload ? withoutDuplicateIds([], action.payload.items) : null
      state.searchTotalCount = action.payload?.totalCount ?? null
      state.searchNextCursor = action.payload?.nextCursor ?? null
      if (action.payload !== null) {
        state.selectedIds = []
      }
    },

    appendSearchResults: (state, action: PayloadAction<FolderItemsPage>) => {
      state.searchResults = [...(state.searchResults ?? []), ...withoutDuplicateIds(state.searchResults ?? [], action.payload.items)]
      state.searchTotalCount = action.payload.totalCount ?? state.searchTotalCount
      state.searchNextCursor = action.payload.nextCursor ?? null
    },

    setIsSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload
    },

    clearSearch: (state) => {
      state.searchQuery = ''
      state.searchResults = null
      state.searchTotalCount = null
      state.searchNextCursor = null
      state.selectedIds = []
    },
  },
})

export const {
  resetFileBrowser,
  setServiceId,
  setFolderView,
  appendFolderItems,
  setCurrentItems,
  setIsInitialLoading,
  setIsFolderLoading,
  toggleItemSelection,
  clearSelection,
  selectAllItems,
  setSearchQuery,
  setSearchResults,
  appendSearchResults,
  setIsSearching,
  clearSearch,
} = documentFileBrowserSlice.actions

export const selectFileBrowserCurrentItems = (state: RootState) => state.documentFileBrowser.currentItems
export const selectFileBrowserTotalCount = (state: RootState) => state.documentFileBrowser.totalCount
export const selectFileBrowserNextCursor = (state: RootState) => state.documentFileBrowser.nextCursor
export const selectFileBrowserSearchTotalCount = (state: RootState) => state.documentFileBrowser.searchTotalCount
export const selectFileBrowserSearchNextCursor = (state: RootState) => state.documentFileBrowser.searchNextCursor
export const selectFileBrowserBreadcrumbs = (state: RootState) => state.documentFileBrowser.breadcrumbs
export const selectFileBrowserSelectedIds = (state: RootState) => state.documentFileBrowser.selectedIds
export const selectFileBrowserSearchQuery = (state: RootState) => state.documentFileBrowser.searchQuery
export const selectFileBrowserSearchResults = (state: RootState) => state.documentFileBrowser.searchResults
export const selectFileBrowserIsInitialLoading = (state: RootState) => state.documentFileBrowser.isInitialLoading
export const selectFileBrowserIsFolderLoading = (state: RootState) => state.documentFileBrowser.isFolderLoading
export const selectFileBrowserIsSearching = (state: RootState) => state.documentFileBrowser.isSearching

export const selectFileBrowserIsInSearchMode = createSelector([selectFileBrowserSearchResults], (searchResults) => searchResults !== null)

export const selectFileBrowserIsAtRoot = createSelector([selectFileBrowserBreadcrumbs], (breadcrumbs) => breadcrumbs.length === 1)

export const selectFileBrowserCurrentFolderId = createSelector([selectFileBrowserBreadcrumbs], (breadcrumbs) => {
  const last = breadcrumbs[breadcrumbs.length - 1]
  return last.id === ROOT_ANCHOR_ID ? null : last.id
})

export const selectFileBrowserCurrentFolderIntegration = createSelector([selectFileBrowserBreadcrumbs], (breadcrumbs) => {
  const last = breadcrumbs[breadcrumbs.length - 1]
  return last.id === ROOT_ANCHOR_ID ? undefined : last.integration
})

export const selectFileBrowserDisplayItems = createSelector(
  [selectFileBrowserIsInSearchMode, selectFileBrowserSearchResults, selectFileBrowserCurrentItems],
  (isInSearchMode, searchResults, currentItems) => sortItems(isInSearchMode ? (searchResults ?? []) : currentItems),
)

export const selectFileBrowserSelectedIdsSet = createSelector([selectFileBrowserSelectedIds], (selectedIds) => new Set(selectedIds))

export const selectFileBrowserSelectedItems = createSelector([selectFileBrowserDisplayItems, selectFileBrowserSelectedIds], (displayItems, selectedIds) => {
  const idSet = new Set(selectedIds)
  return displayItems.filter((item) => idSet.has(item.id))
})

export default documentFileBrowserSlice.reducer
